import React, { Component, createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { fetchAuthSession, signOut as cognitoSignOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import JournalPage from './pages/JournalPage';
import NewEntryPage from './pages/NewEntryPage';
import EntryDetailPage from './pages/EntryDetailPage';
import ProfilePage from './pages/ProfilePage';
import AiToolsPage from './pages/AiToolsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import NotFoundPage from './pages/NotFoundPage';
import LandingPage from './pages/LandingPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import FaqPage from './pages/FaqPage';
import PricingPage from './pages/PricingPage';
import apiClient from './api/client';
import { setAuthErrorHandler } from './api/client';
import CookieConsent from './components/CookieConsent';
import TrialBanner from './components/TrialBanner';
import TrialEndedBanner from './components/TrialEndedBanner';
import { trackEvent } from './utils/analytics';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '60vh', padding: '2rem',
          textAlign: 'center',
        }}>
          <div style={{
            backgroundColor: '#f0fdfa', border: '1px solid #99d5cf',
            borderRadius: '12px', padding: '2.5rem', maxWidth: '480px',
          }}>
            <h2 style={{ color: '#01696F', margin: '0 0 0.75rem', fontSize: '1.25rem' }}>
              Something went wrong
            </h2>
            <p style={{ color: '#475569', margin: '0 0 1.5rem', fontSize: '0.95rem', lineHeight: 1.5 }}>
              An unexpected error occurred. Please reload the page to try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#01696F', color: '#ffffff', border: 'none',
                borderRadius: '8px', padding: '0.65rem 1.5rem', fontSize: '0.95rem',
                fontWeight: '600', cursor: 'pointer',
              }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function SkipLink() {
  const [focused, setFocused] = useState(false);
  return (
    <a
      href="#main-content"
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        position: 'fixed', top: focused ? '10px' : '-100px', left: '10px',
        backgroundColor: '#01696F', color: '#ffffff', padding: '0.5rem 1rem',
        borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600',
        textDecoration: 'none', zIndex: 10000,
        transition: 'top 0.2s',
      }}
    >
      Skip to main content
    </a>
  );
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem('bragsheet_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = sessionStorage.getItem('bragsheet_token');
    if (storedToken) {
      // Attempt to refresh the Cognito session token before calling the profile API
      fetchAuthSession({ forceRefresh: false })
        .then(session => {
          const freshToken = session.tokens && session.tokens.idToken
            ? session.tokens.idToken.toString()
            : null;
          if (freshToken) {
            sessionStorage.setItem('bragsheet_token', freshToken);
          }
        })
        .catch(() => {
          // No active Cognito session — fall through to profile check which will 401 and clear state
        })
        .finally(() => {
          apiClient.get('/api/profile')
            .then(res => {
              if (res.data && res.data.data) {
                setUser(res.data.data.user);
              }
            })
            .catch(() => {
              sessionStorage.removeItem('bragsheet_token');
              setToken(null);
            })
            .finally(() => setLoading(false));
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback((newToken, newUser) => {
    sessionStorage.setItem('bragsheet_token', newToken);
    setToken(newToken);
    setUser(newUser);
  }, []);

  const logout = useCallback(async () => {
    try { await cognitoSignOut(); } catch (_) {}
    sessionStorage.removeItem('bragsheet_token');
    setToken(null);
    setUser(null);
    setSessionWarning(null);
  }, []);

  useEffect(() => {
    setAuthErrorHandler(() => {
      sessionStorage.removeItem('bragsheet_token');
      setToken(null);
      setUser(null);
      setSessionWarning(null);
    });
  }, []);

  // Multi-tab sync listener removed: sessionStorage is tab-scoped and does not
  // fire the 'storage' event across tabs, so cross-tab token sync is unnecessary.

  const [sessionWarning, setSessionWarning] = useState(null);

  const refreshSession = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const freshToken = session.tokens?.idToken?.toString();
      if (freshToken) {
        sessionStorage.setItem('bragsheet_token', freshToken);
        setToken(freshToken);
        setSessionWarning(null);
      }
    } catch {
      setSessionWarning('expired');
    }
  }, []);

  useEffect(() => {
    if (!token) return;

    const checkExpiry = () => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp * 1000;
        const remaining = exp - Date.now();

        if (remaining <= 0) {
          setSessionWarning('expired');
        } else if (remaining < 5 * 60 * 1000) {
          setSessionWarning('expiring');
          refreshSession();
        }
      } catch {
        sessionStorage.removeItem('bragsheet_token');
        setToken(null);
        setUser(null);
      }
    };

    const intervalId = setInterval(checkExpiry, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [token, refreshSession]);

  // Listen for OAuth redirect sign-in events
  useEffect(() => {
    const hubListener = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        try {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken?.toString() || null;
          const tokenPayload = session.tokens?.idToken?.payload || {};
          const userPayload = {
            id: tokenPayload.sub,
            email: tokenPayload.email,
            name: tokenPayload.name || tokenPayload['cognito:username'] || '',
          };
          login(idToken, userPayload);
        } catch (err) {
          console.error('OAuth redirect sign-in failed:', err);
        }
      }
      if (payload.event === 'signInWithRedirect_failure') {
        console.error('OAuth redirect failed:', payload.data);
      }
    });

    return () => hubListener();
  }, [login]);

  const isAuthenticated = Boolean(token && user);

  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Analytics: fire `trial_started` the first time per session that
  // /api/subscription/status reports status='trialing'. Use a ref so the
  // check doesn't trigger re-renders and survives across multiple effect
  // runs. Also detect active→canceled transitions for `subscription_canceled`.
  const trialStartedFiredRef = useRef(false);
  const lastSubscriptionStatusRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setSubscriptionStatus(null);
      return;
    }
    apiClient.get('/api/subscription/status')
      .then(res => {
        if (res.data?.data) {
          const next = res.data.data;
          // trial_started: first observation of 'trialing' in this session.
          if (next.status === 'trialing' && !trialStartedFiredRef.current) {
            trialStartedFiredRef.current = true;
            trackEvent('trial_started');
          }
          // subscription_canceled: active → canceled transition. The user
          // typically cancels via Stripe customer portal and comes back to
          // dashboard; on the next status fetch we observe the flip.
          const prev = lastSubscriptionStatusRef.current;
          if (prev === 'active' && next.status === 'canceled') {
            trackEvent('subscription_canceled');
          }
          lastSubscriptionStatusRef.current = next.status;
          setSubscriptionStatus(next);
        }
      })
      .catch(() => {
        // Subscription endpoint may not exist yet — ignore
      });
  }, [isAuthenticated]);

  const trialDaysLeft = subscriptionStatus?.status === 'trialing' ? subscriptionStatus.trialDaysLeft : null;
  const isActiveSubscriber = subscriptionStatus?.status === 'active';
  // Show post-trial banner when the trial has ended. Two signals:
  //   1. Server-side flip: trial-reminder Lambda sets status='free' on expiry.
  //   2. Client-side detection: status still 'trialing' but trialDaysLeft <= 0
  //      (covers the window between trial end and the next Lambda run).
  const trialEnded = isAuthenticated && subscriptionStatus && (
    (subscriptionStatus.status === 'free' && subscriptionStatus.plan === 'free') ||
    (subscriptionStatus.status === 'trialing' && subscriptionStatus.trialDaysLeft != null && subscriptionStatus.trialDaysLeft <= 0)
  );

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, login, logout, loading, sessionWarning, refreshSession, subscriptionStatus }}>
      <SkipLink />
      {isAuthenticated && trialDaysLeft != null && trialDaysLeft > 0 && !isActiveSubscriber && (
        <TrialBanner daysLeft={trialDaysLeft} />
      )}
      {trialEnded && !isActiveSubscriber && <TrialEndedBanner />}
      {sessionWarning && (
        <div role="alert" style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          padding: '0.75rem 1.5rem',
          backgroundColor: sessionWarning === 'expiring' ? '#fffbeb' : '#fef2f2',
          borderBottom: `1px solid ${sessionWarning === 'expiring' ? '#fde68a' : '#fecaca'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem',
          fontSize: '0.875rem',
          color: sessionWarning === 'expiring' ? '#92400e' : '#ef4444',
        }}>
          <span>
            {sessionWarning === 'expiring'
              ? 'Your session is about to expire.'
              : 'Your session has expired. Please sign in again.'}
          </span>
          {sessionWarning === 'expiring' ? (
            <button onClick={refreshSession} style={{
              padding: '0.35rem 0.75rem', borderRadius: '6px', border: 'none',
              backgroundColor: '#01696F', color: '#ffffff', fontSize: '0.8rem',
              fontWeight: '600', cursor: 'pointer',
            }}>Stay signed in</button>
          ) : (
            <a href="/login" style={{
              padding: '0.35rem 0.75rem', borderRadius: '6px',
              backgroundColor: '#ef4444', color: '#ffffff', fontSize: '0.8rem',
              fontWeight: '600', textDecoration: 'none',
            }}>Sign in</a>
          )}
        </div>
      )}
      {children}
      <CookieConsent />
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <p style={{ color: '#64748b', fontSize: '1rem' }}>Loading...</p>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <p style={{ color: '#64748b', fontSize: '1rem' }}>Loading...</p>
      </div>
    );
  }
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function RootRedirect() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <p style={{ color: '#64748b', fontSize: '1rem' }}>Loading...</p>
      </div>
    );
  }
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

// Lightweight client-side error tracking
(function initErrorTracking() {
  let errorCount = 0;
  let windowStart = Date.now();
  const MAX_ERRORS_PER_MINUTE = 5;

  function resetWindowIfNeeded() {
    const now = Date.now();
    if (now - windowStart > 60000) {
      errorCount = 0;
      windowStart = now;
    }
  }

  function reportError(message, stack) {
    resetWindowIfNeeded();
    if (errorCount >= MAX_ERRORS_PER_MINUTE) return;
    errorCount++;

    const token = sessionStorage.getItem('bragsheet_token');
    if (!token) return;

    const payload = JSON.stringify({
      message: String(message || 'Unknown error').slice(0, 500),
      stack: String(stack || '').slice(0, 2000),
      url: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    });

    // Fire and forget — fail silently on 404 or any error
    try {
      fetch('/api/errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: payload,
      }).catch(() => {});
    } catch (_) {}
  }

  window.onerror = function (message, source, lineno, colno, error) {
    reportError(
      message,
      error && error.stack ? error.stack : 'at ' + source + ':' + lineno + ':' + colno
    );
  };

  window.onunhandledrejection = function (event) {
    const reason = event.reason;
    reportError(
      reason && reason.message ? reason.message : String(reason),
      reason && reason.stack ? reason.stack : ''
    );
  };
})();

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/verify-email" element={<PublicRoute><VerifyEmailPage /></PublicRoute>} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/signin" element={<Navigate to="/login" replace />} />
          <Route path="/signup" element={<Navigate to="/register" replace />} />
          <Route path="/reset-password" element={<Navigate to="/forgot-password" replace />} />
          <Route
            path="/dashboard"
            element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
          />
          <Route
            path="/journal"
            element={<ProtectedRoute><JournalPage /></ProtectedRoute>}
          />
          <Route
            path="/journal/new"
            element={<ProtectedRoute><NewEntryPage /></ProtectedRoute>}
          />
          <Route
            path="/journal/:id"
            element={<ProtectedRoute><EntryDetailPage /></ProtectedRoute>}
          />
          <Route
            path="/ai-tools"
            element={<ProtectedRoute><AiToolsPage /></ProtectedRoute>}
          />
          <Route
            path="/profile"
            element={<ProtectedRoute><ProfilePage /></ProtectedRoute>}
          />
          {/* /settings is an alias for /profile — keeps any external links
              or muscle-memory navigations from hitting the 404 page. */}
          <Route path="/settings" element={<Navigate to="/profile" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}
